import { Component, inject, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from "@angular/forms";
import { Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { HttpErrorResponse } from "@angular/common/http";
import { CommonModule } from "@angular/common";

import { AuthService, JwtService } from "@app/core/auth";
import { APP_ROUTES } from "@app/constants";
import { GenericResponse } from "@app/core/backend/api-response.type";
import { TenantService } from "@app/core/tenant/tenant.service";
import { UtilService } from "@app/modules/shared/service/util.service";
import { FieldValidatorService } from "@app/modules/shared/service/field-validator.service";

import { FormComponent } from "@app/modules/shared/components/form/form.component";
import { FooterComponent } from "@app/layout/app-layout/footer/footer.component";
import { PasswordModule } from "primeng/password";
import { ButtonModule } from "primeng/button";
import { RippleModule } from "primeng/ripple";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";

import * as jwt_decode from "jwt-decode";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { ChangePasswordComponent } from "@app/modules/admin/system-user/change-password/change-password.component";
import { environment } from "@app/environments/environment";

@Component({
  selector: "app-login",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PasswordModule,
    ButtonModule,
    RippleModule,
    CheckboxModule,
    InputTextModule,
    FooterComponent,
    FormComponent,
  ],
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.css"],
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  passwordFieldType: string = "password";
  loggingIn = false;
  tenant = inject(TenantService);
  ref: DynamicDialogRef | undefined;
  private readonly utilService = inject(UtilService);

  public readonly sideImageUrl: string = `${this.utilService.getNormalizedTenantCdnBaseUrl()}/assets/sideimage.png`;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly authService: AuthService,
    private readonly dialogService: DialogService,
    private readonly jwtService: JwtService,
    private readonly utilServices: UtilService,
    private readonly fieldValidatorService: FieldValidatorService
  ) {}

  ngOnInit() {
    this.loginForm = this.formBuilder.group({
      username: new FormControl("", Validators.required),
      password: new FormControl("", Validators.required),
      rememberMe: new FormControl(false),
    });
  }

  togglePasswordVisibility() {
    this.passwordFieldType =
      this.passwordFieldType === "password" ? "text" : "password";
  }

  showError(formControlName: string) {
    return this.utilServices.showError(formControlName, this.loginForm);
  }

  getErrorMessage(title: string): string {
    return this.utilServices.getErrorMessage(title, this.loginForm);
  }

  login() {
    this.utilServices.validateForm(this.loginForm);

    if (this.loginForm.invalid) return; // stop if invalid

    this.loggingIn = true;

    const credentials = {
      username: this.loginForm.value.username,
      password: this.loginForm.value.password,
    };

    this.authService.login(credentials).subscribe({
      next: (response) => {
        this.loggingIn = false;

        if (response.data?.access_token) {
          this.messageService.add({
            key: "message",
            severity: "success",
            summary: "Login Success",
            detail: response.message,
          });

          const tokens: any = jwt_decode.jwtDecode(response.data.access_token);

          if (tokens.passwordChangeRequired) {
            this.openChangePasswordPage(credentials.username);
          } else {
            this.router.navigate([APP_ROUTES.DASHBOARD]);
          }
        } else {
          this.messageService.add({
            key: "message",
            severity: "error",
            summary: "Login Error",
            detail: "Failed to Login",
          });
        }
      },
      error: (errorResponse: HttpErrorResponse) => {
        this.loggingIn = false; // <-- important: re-enable button on error

        const errorData: GenericResponse<any> = errorResponse.error;
        this.messageService.add({
          key: "message",
          severity: "error",
          summary: "Login Error",
          detail: errorData?.message || "Failed to Login",
        });
      },
    });
  }

  openChangePasswordPage(username: string) {
    this.ref = this.dialogService.open(ChangePasswordComponent, {
      data: username,
      header: "Change Password",
      width: "50%",
      contentStyle: { overflow: "auto" },
      baseZIndex: 10000,
      maximizable: true,
      modal: true,
    });
  }
}
